import { Label, RichText, Vec3, _decorator, Node } from 'cc';
import LocalizedLabel from '../LocalizedComponent/LocalizedLabel';
import Page from '../Page/Page';
import Dialog from './Dialog';
import GameDependency from '../../../System/GameDependency';
import { ErrorType } from '../../../ErrorHandler/ErrorHandler';
import LocalizedRichText from '../LocalizedComponent/LocalizedRichText';
import { MessageType } from '../../../MessageManager';

const { ccclass, property, requireComponent } = _decorator;

@ccclass('MessageDialog')
@requireComponent(Dialog)
export default class MessageDialog extends GameDependency {
    @property(Label)
    protected readonly title: Label = null;

    @property(Label)
    protected readonly content: Label = null;

    @property(RichText)
    protected readonly contentRichText: RichText = null;

    @property(Label)
    protected readonly errorCodeLabel: Label = null;

    @property
    protected readonly adjustConfirmButtonPosition: boolean = true;

    @property({
        type: [Vec3],
        visible(this: MessageDialog) {
            return this.adjustConfirmButtonPosition;
        },
    })
    private readonly confirmButtonPositions: Vec3[] = [];

    @property
    private readonly openSoundEffectName: string = '';

    private _page: Page = null;
    private _dialog: Dialog = null;

    private _nodeMap: Map<Node, LocalizedLabel | LocalizedRichText> = new Map();

    private _playSoundOnOpen = true;

    protected get dialog() {
        return this._dialog;
    }

    protected get page() {
        return this._page;
    }

    public async onLoad() {
        this._page = this.getComponent(Page);
        this._dialog = this.getComponent(Dialog);

        this.node.on(Page.EVENT_TYPE.OPEN_START, () => {
            if (this._playSoundOnOpen && this.openSoundEffectName) {
                this.game.audio.playEffect(this.openSoundEffectName);
            }
        });
    }

    public display(title: string, message: string, errorCode?: string, showCancel = false, playSoundOnOpen = true) {
        // 當 MessageType 為 Message 時，即使 errorCode 為空，也不應隱藏 confirm 按鈕
        const isMessageType = !errorCode || errorCode === MessageType.MESSAGE;
        if (!isMessageType && this.game.error.getErrorEntity(message).type === ErrorType.Error && this.game.config.hideExit) {
            this.hideConfirmButton();
        }
        if (this._dialog.cancelButton) {
            this._dialog.cancelButton.node.active = showCancel;
        }
        if (this._dialog.confirmButton && this.adjustConfirmButtonPosition) {
            this._dialog.confirmButton.node.setPosition(this.confirmButtonPositions[showCancel ? 1 : 0]);
        }

        this.handleTitle(title);
        this.handleContent(message, errorCode);
        this.handleErrorCode(errorCode);

        this._playSoundOnOpen = playSoundOnOpen;
        this._page.open();
    }

    public forceClose() {
        this._page.close();
    }

    protected hideConfirmButton() {
        if (this._dialog.confirmButton) {
            this._dialog.confirmButton.node.active = false;
        }
    }

    protected handleTitle(title: string) {
        if (title) {
            this._setLocalizedMessageWithErrorCode(this.title, title);
        }
    }

    protected handleContent(message: string, errorCode: string) {
        this._setLocalizedMessageWithErrorCode(this.content, message, errorCode);
        this._setLocalizedMessageWithErrorCode(this.contentRichText, message, errorCode);
    }

    protected handleErrorCode(errorCode: string) {
        if (errorCode !== undefined) {
            this._setLocalizedMessageWithErrorCode(this.errorCodeLabel, errorCode);
        }
    }

    private _setLocalizedMessageWithErrorCode(target: Label | RichText, message: string, errorCode?: string) {
        const localizedTarget = this._getLocalizedComponent(target);
        if (localizedTarget) {
            if (errorCode !== undefined) {
                localizedTarget.key = `${message}|${errorCode}`;
            } else {
                localizedTarget.key = message;
            }
        } else if (target) {
            target.string = message;
        }
    }

    private _getLocalizedComponent(target: Label | RichText): LocalizedLabel | LocalizedRichText | null {
        if (target) {
            const { node } = target;
            let localizedTarget: LocalizedLabel | LocalizedRichText = this._nodeMap.get(node);
            if (localizedTarget) {
                return localizedTarget;
            }
            localizedTarget = target.getComponent(LocalizedLabel) ?? target.getComponent(LocalizedRichText);
            this._nodeMap.set(node, localizedTarget);
            return localizedTarget;
        }
        return null;
    }
}
